/* assets/hrb-trapfix.js — minimal, safe wrapper for Dawn focus trap (v2)
   - Load this AFTER assets/global.js
   - Makes trapFocus/addTrapFocus null-safe and tolerant of empty containers
   - Does NOT block or change menu behavior; only prevents crashes
   - Enables debug logs with ?menu_debug=1
*/
(() => {
  const DEBUG = /\bmenu_debug=1\b/.test(location.search);
  const log = (...a) => { if (DEBUG) console.log('[HRB trapfix]', ...a); };

  const FOCUS_SEL = "summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe";

  function getFocusables(root) {
    try {
      if (!root || !root.querySelectorAll) return [];
      const list = Array.from(root.querySelectorAll(FOCUS_SEL));
      return list.filter(el => !el.closest('[aria-hidden=\"true\"], [inert]'));
    } catch { return []; }
  }

  function ensureSentinel(root) {
    if (!root || !root.querySelector) return null;
    let sent = root.querySelector('.hrb-focus-sentinel');
    if (!sent) {
      sent = document.createElement('button');
      sent.type = 'button';
      sent.tabIndex = 0;
      sent.className = 'hrb-focus-sentinel';
      sent.setAttribute('aria-label', 'Navigation focus helper');
      // visually hidden, but accessible
      sent.style.cssText = [
        'position:absolute','left:-10000px','top:auto','width:1px','height:1px',
        'overflow:hidden','clip:rect(1px, 1px, 1px, 1px)','clip-path:inset(50%)',
        'white-space:nowrap','border:0','padding:0','margin:0'
      ].join(';');
      try { root.prepend(sent); } catch {}
      log('sentinel added', root);
    }
    return sent;
  }

  function safeFocus(el) { try { el && el.focus && el.focus(); } catch {} }

  // ---- Wrap window.trapFocus (keep original behavior, add guards) ----
  const origTrap = window.trapFocus;
  if (typeof origTrap === 'function') {
    window.trapFocus = function(container, elementToFocus) {
      try {
        if (!container) return;
        // If no focusables yet, add a sentinel so theme code doesn't crash
        const focusables = getFocusables(container);
        if (!focusables.length) {
          const sent = ensureSentinel(container);
          if (elementToFocus == null) elementToFocus = sent;
        } else if (elementToFocus == null) {
          elementToFocus = focusables[0];
        }
        return origTrap.call(this, container, elementToFocus);
      } catch (e) {
        log('guarded trapFocus', e);
        // Fallback: try to focus something safe so flow can continue
        const f = getFocusables(container);
        safeFocus(f[0] || ensureSentinel(container) || container);
      }
    };
    log('wrapped window.trapFocus');
  } else {
    log('no window.trapFocus found; skipping wrap');
  }

  // ---- Wrap HTMLElement.prototype.addTrapFocus (guard errors only) ----
  if (window.HTMLElement && HTMLElement.prototype) {
    const origAdd = HTMLElement.prototype.addTrapFocus;
    if (typeof origAdd === 'function' && !origAdd.__hrbShimWrapped) {
      HTMLElement.prototype.addTrapFocus = function() {
        try {
          // If empty, ensure a sentinel so theme's focus code has a target
          const f = getFocusables(this);
          if (!f.length) ensureSentinel(this);
          return origAdd.apply(this, arguments);
        } catch (e) {
          log('guarded addTrapFocus', e);
          safeFocus(getFocusables(this)[0] || ensureSentinel(this) || this);
        }
      };
      HTMLElement.prototype.addTrapFocus.__hrbShimWrapped = true;
      log('wrapped HTMLElement.prototype.addTrapFocus');
    } else {
      log('no addTrapFocus on HTMLElement.prototype; skipping wrap');
    }
  }
})();